import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { PdfExportService } from '../forces/services/pdf-export.service';
import { ToastService } from '../global/toast/toast';

import { EVENT_PACK_CSS } from './event-pack-css';
import { AppEvent } from './models';

/**
 * Turns a rendered event pack document into a PDF.
 *
 * Shared because two callers export the same DOM: the event detail page, which renders the
 * document hidden and downloads straight away, and the preview route. Keeping the export options
 * in one place is what stops those two producing different documents.
 */
@Injectable({ providedIn: 'root' })
export class EventPackService {
    private pdfExportService = inject(PdfExportService);
    private translate = inject(TranslateService);
    private toast = inject(ToastService);

    /** Resolves true when the PDF was produced, false when it failed (already reported). */
    async download(element: HTMLElement, event: AppEvent): Promise<boolean> {
        try {
            await this.pdfExportService.exportElement(element, this.filename(event), {
                paginate: true,
                // The app's own stylesheets are withheld deliberately: paged.js measures the page
                // boxes it builds and Ionic's globals distort them enough to halt its layout loop,
                // which yields blank pages. EVENT_PACK_CSS is therefore the complete stylesheet.
                onlyExtraCss: true,
                extraCss: EVENT_PACK_CSS
            });
            return true;
        } catch (error: any) {
            const message = error?.message || this.translate.instant('GLOBAL.EVENTS.PACK.FAILED');
            this.toast.showToast('', message, { color: 'danger', duration: 4000 });
            return false;
        }
    }

    private filename(event: AppEvent): string {
        const name = (event.title || 'Event').replace(/[^\w\s-]/g, '').trim();
        return `${name} - Event Pack`;
    }
}
